import { useEffect, useState } from "react"
import { motion } from "framer-motion"
import { useNavigate } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import Lottie from "lottie-react"
import {
  IconDotsVertical,
  IconLogout,
  IconNotification,
  IconSettings,
  IconUserCircle,
} from "@tabler/icons-react"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar"
import { useAuth } from "@/contexts/AuthContext"
import { Badge } from "@/components/ui/badge"
import { NotificationService } from "@/services/notification.service"

export function NavUser({
  user,
}: {
  user: {
    name: string
    email: string
    avatar: string
  }
}) {
  const { isMobile } = useSidebar()
  const { logout, isAuthenticated } = useAuth()
  const navigate = useNavigate()

  // Real-time unread notification count. Polls every 30s while the user is
  // logged in; the query is auto-disabled on logout so we don't spam the API.
  const { data: unreadData } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => NotificationService.getUnreadCount(),
    enabled: isAuthenticated,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    staleTime: 15_000,
  })
  const unreadCount = unreadData?.data?.count ?? 0

  const getInitials = (name?: string | null) => {
    return (name ?? "")
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2)
  }

  // Logout flow now opens an AlertDialog with a Lottie animation that
  // asks the admin to confirm. Only the Logout action inside the dialog
  // actually signs them out.
  const [logoutOpen, setLogoutOpen] = useState(false)
  const [loggingOut, setLoggingOut] = useState(false)
  const [logoutLottie, setLogoutLottie] = useState<Record<string, unknown> | null>(null)

  // Fetch the logout Lottie JSON from /public on first mount. The file
  // is small (~22 KB) and sits in /public/lottie so the dev server and
  // the production build both serve it from the same path. Storing the
  // parsed JSON in state means subsequent dialog opens are instant.
  useEffect(() => {
    let cancelled = false
    fetch('/lottie/logout.json')
      .then((r) => r.json())
      .then((j) => { if (!cancelled) setLogoutLottie(j) })
      .catch(() => { /* non-fatal — dialog falls back to text-only */ })
    return () => { cancelled = true }
  }, [])

  const handleConfirmLogout = async () => {
    setLoggingOut(true)
    try {
      await logout()
      navigate("/")
    } finally {
      setLoggingOut(false)
      setLogoutOpen(false)
    }
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <motion.div whileHover={{ scale: 1.05 }} transition={{ type: "spring", stiffness: 400 }}>
                <Avatar className="h-8 w-8 rounded-lg ring-2 ring-primary/20">
                  <AvatarImage src={user.avatar} alt={user.name} />
                  <AvatarFallback className="rounded-lg bg-primary text-primary-foreground">
                    {getInitials(user.name)}
                  </AvatarFallback>
                </Avatar>
              </motion.div>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">{user.name}</span>
                <span className="text-muted-foreground truncate text-xs flex items-center gap-1">
                  <Badge variant="outline" className="text-[10px] px-1 py-0 h-4 capitalize">
                    Super Admin
                  </Badge>
                </span>
              </div>
              <IconDotsVertical className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-xl"
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-3 px-2 py-3 text-left text-sm">
                <Avatar className="h-10 w-10 rounded-lg ring-2 ring-primary/20">
                  <AvatarImage src={user.avatar} alt={user.name} />
                  <AvatarFallback className="rounded-lg bg-primary text-primary-foreground">
                    {getInitials(user.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">{user.name}</span>
                  <span className="text-muted-foreground truncate text-xs">{user.email}</span>
                  <Badge variant="secondary" className="mt-1 w-fit text-[10px] px-1.5 py-0 h-4 capitalize">
                    Super Admin
                  </Badge>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem onClick={() => navigate("/profile")} className="cursor-pointer">
                <IconUserCircle className="mr-2" />
                My Profile
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/notifications")} className="cursor-pointer">
                <IconNotification className="mr-2" />
                Notifications
                {unreadCount > 0 && (
                  <Badge className="ml-auto bg-primary/10 text-primary hover:bg-primary/10 text-xs">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </Badge>
                )}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/settings")} className="cursor-pointer">
                <IconSettings className="mr-2" />
                Settings
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => setLogoutOpen(true)}
              className="cursor-pointer text-destructive focus:text-destructive"
            >
              <IconLogout className="mr-2" />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>

      {/* Logout confirmation — Lottie animation + "Are you sure?" copy.
          The Logout action button is red so the destructive intent is
          obvious; Cancel keeps the user signed in. */}
      <AlertDialog open={logoutOpen} onOpenChange={(v) => !loggingOut && setLogoutOpen(v)}>
        <AlertDialogContent className="sm:max-w-md">
          {logoutLottie && (
            <div className="mx-auto w-40 h-40 sm:w-48 sm:h-48 -mt-2">
              <Lottie animationData={logoutLottie} loop autoplay className="w-full h-full" />
            </div>
          )}
          <AlertDialogHeader className="text-center space-y-2">
            <AlertDialogTitle className="text-xl">
              Are you sure you want to logout?
            </AlertDialogTitle>
            <AlertDialogDescription>
              You'll need to sign in again to access the admin panel.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row justify-center gap-2 sm:gap-3">
            <AlertDialogCancel disabled={loggingOut} className="rounded-full">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmLogout}
              disabled={loggingOut}
              className="rounded-full bg-red-500 hover:bg-red-600 text-white"
            >
              {loggingOut ? "Logging out…" : "Logout"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SidebarMenu>
  )
}
